import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { withApollo } from 'react-apollo'
import { withRouter } from 'react-router'

import { compose, getContext, withHandlers, withState } from 'recompose'

import CenteredSpinner from 'components/CenteredSpinner'
import withPartnerProps, { WithPartnerProps } from 'components/HOCs/WithPartnerProps'
import withRemoteStrings from 'components/HOCs/withRemoteStrings'
import { ProductKey } from 'generated/types'
import { EnvironmentProps, withEnv } from 'utils/environment'
import { sendToSentry } from 'utils/errors'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getFlow, isDefaultFlow } from 'utils/FlowProvider/utils'
import { WithOptimizelyProps, withOptimizely } from 'utils/Optimizely'
import removeDuplicateQueryParams from 'utils/sourceAttributes'

import tierGroupQuery from './gql/tierGroup.query.gql'
import tierOptionByProductKeyQuery from './gql/tierOptionByProductKey.query.gql'
import tierOptionByTierPriceIdQuery from './gql/tierOptionByTierPriceId.query.gql'
import tierSubscriptionForUserQuery from './gql/tierSubscriptionForUser.query.gql'
import {
  getProductKey,
  getTierNameFromOption,
  getTierPriceFromOption,
  getTierPriceId,
  setTierName,
  setTierPrice,
  setTierPriceIdCookie,
} from './utils'
import { getTierKeyFromQueryString } from './utils'

type InnerProps = {
  tierPrice: number | null
  shouldGetTierInfo: boolean
  tierPriceId: string | null
  shouldGetTierPriceIdForProductFunnel: boolean
  isEarlyTier: boolean
}

type Props = InnerProps &
  WithTieredRegistrationProps &
  WithOptimizelyProps &
  WithPartnerProps &
  EnvironmentProps

// This provider handles all of the logic associated with Tiered Registration
class Provider extends PureComponent<Props, InnerProps> {
  public static childContextTypes = {
    isTieredRegEnabled: PropTypes.bool.isRequired,
    tierPriceId: PropTypes.string,
    tierPrice: PropTypes.number,
    isEarlyTier: PropTypes.bool.isRequired,
    setTierPriceId: PropTypes.func,
  }

  protected tierPriceId: string
  protected tierKey: string
  protected referralCode: string
  protected isTieredRegEnabled: boolean
  protected isPartnerReg: boolean
  protected signUp: boolean
  protected redirectToTierSelection: boolean
  protected selectTierPathname: string
  protected signUpName: string

  constructor(props) {
    super(props)
    const params = removeDuplicateQueryParams(location.search)

    this.referralCode = params.referral_code
    this.tierPriceId = params.tierPriceId || getTierPriceId()
    this.tierKey = params.tierKey || getTierKeyFromQueryString()
    this.isTieredRegEnabled = props.isFeatureEnabled('tiered_registration')
    this.selectTierPathname = FlowRoute.SelectTier
    this.signUpName = FlowRoute.Signup

    if (this.isTieredRegEnabled && this.props.isPartnerReg) {
      setTierPrice(1)
    }

    this.redirectToTierSelection = window.location.pathname === this.selectTierPathname

    this.signUp =
      !this.tierPriceId &&
      !this.tierKey &&
      !this.referralCode &&
      !this.props.isPartnerReg &&
      !this.selectTierPathname &&
      this.isTieredRegEnabled &&
      isDefaultFlow() &&
      window.location.pathname !== this.signUpName

    if (this.signUp) {
      this.handleRedirectTosignUp()
    }

    this.state = {
      tierPrice: null,
      shouldGetTierInfo:
        this.isTieredRegEnabled &&
        isDefaultFlow() &&
        !this.props.isPartnerReg &&
        (!!this.tierPriceId || !!this.tierKey),
      tierPriceId: isDefaultFlow() ? this.tierPriceId : null,
      shouldGetTierPriceIdForProductFunnel: this.isTieredRegEnabled && !isDefaultFlow(),
      isEarlyTier: false,
    }
  }

  public componentDidMount() {
    this.getTierPriceAndIdByProductKey()
    if (!this.state.tierPriceId) {
      this.props.client
        .query({ query: tierGroupQuery, fetchPolicy: 'network-only' })
        .then(response => {
          const tierOption = response?.data?.tierGroup.tierOptions.find(
            option => option.tier.id === response.data.tierGroup.preferredTierId,
          )
          const tierPrice = tierOption?.preferredTierPrice
          const tierName = tierOption?.tier.name
          const isEarlyTier = tierOption?.tier.products.some(
            product => product.key === ProductKey.Early,
          )

          setTierPrice(tierPrice?.amount.value ?? null)
          setTierName(tierName ?? null)
          tierPrice?.id && setTierPriceIdCookie(tierPrice.id)

          this.setState({
            tierPrice: tierPrice?.amount.value ?? null,
            tierPriceId: tierPrice?.id,
            shouldGetTierInfo: false,
            isEarlyTier,
          })
        })
        .catch(error => {
          sendToSentry(error, {
            name: 'TieredRegistration Provider',
            message: 'attempting to get tierOptionByTierPriceId query',
          })
        })
    } else {
      this.getTierInfoByTierPriceId()
    }
  }

  public getChildContext() {
    const { isTieredRegEnabled, setTierPriceId } = this
    const { tierPrice, tierPriceId, isEarlyTier } = this.state

    return {
      isTieredRegEnabled,
      tierPriceId: isDefaultFlow() && this.tierPriceId ? this.tierPriceId : tierPriceId,
      tierPrice,
      isEarlyTier,
      setTierPriceId,
    }
  }

  public setTierPriceId = (tierPriceId: string) => {
    this.setState({ tierPriceId }, () => {
      if (this.tierPriceId) {
        this.tierPriceId = tierPriceId
      }
      this.getTierInfoByTierPriceId()
    })
  }

  public render() {
    if (this.isLoading()) {
      return <CenteredSpinner />
    }
    return <>{this.props.children}</>
  }

  private isLoading = (): boolean =>
    this.signUp || this.state.shouldGetTierInfo || this.state.shouldGetTierPriceIdForProductFunnel

  private handleRedirectTosignUp = (): void => {
    if (getFlow() !== 'core') {
      window.location.replace(`${this.signUpName}${window.location.search}`)
    } else {
      return
    }
  }

  private getTierInfoByTierPriceId = () => {
    this.props.client
      .query({
        query: tierOptionByTierPriceIdQuery,
        variables: {
          tierPriceId:
            isDefaultFlow() && this.tierPriceId ? this.tierPriceId : this.state.tierPriceId,
        },
      })
      .then(tierOption => {
        const isInvalidTierPriceId =
          tierOption?.data?.tierOptionByTierPriceId?.__typename === 'InvalidTierPriceId'
        if (isInvalidTierPriceId && window.location.pathname !== this.selectTierPathname) {
          window.location.replace(this.selectTierPathname)
        }

        const tierPrice = getTierPriceFromOption(tierOption)
        setTierPrice(tierPrice?.amount?.value ?? null)
        const tierName = getTierNameFromOption(tierOption)
        setTierName(tierName ?? null)

        const isEarlyTier = this.checkForEarlyTier(tierOption)
        this.setState({
          tierPrice: tierPrice?.amount.value ?? null,
          shouldGetTierInfo: false,
          isEarlyTier,
        })
      })
      .catch(error => {
        sendToSentry(error, {
          name: 'TieredRegistration Provider',
          message: 'attempting to get tierOptionByTierPriceId query',
        })
      })
  }

  private getTierPriceAndIdByProductKey = () => {
    if (!isDefaultFlow()) {
      const productKey = getProductKey()
      this.props.client
        .query({
          query: tierOptionByProductKeyQuery,
          variables: { productKey },
        })
        .then(tierOption => {
          const tierPrice = getTierPriceFromOption(tierOption)
          setTierPrice(tierPrice?.amount?.value ?? null)
          this.setState({
            tierPriceId: tierPrice?.id ?? null,
            shouldGetTierPriceIdForProductFunnel: false,
            tierPrice: tierPrice?.amount?.value ?? null,
          })
        })
        .catch(error => {
          sendToSentry(error, {
            name: 'TieredRegistration Provider',
            message: 'attempting to get tierOptionByProductKey query',
          })
          this.setState({ shouldGetTierPriceIdForProductFunnel: false })
        })
    }
    return null
  }

  private checkForEarlyTier = tierOption => {
    const tierKeys = tierOption?.data?.tierOptionByTierPriceId?.tier?.includedProducts || []
    return tierKeys.some(product => product.key === ProductKey.Early)
  }
}

export const TieredRegistrationProvider = compose(
  withOptimizely,
  withPartnerProps,
  withEnv,
  withRouter,
  withRemoteStrings,
)(Provider)

// This is the HOC for using on the enhanced components.
export const withTieredRegistration = compose<Props, {}>(
  withApollo,
  withEnv,
  getContext({
    isTieredRegEnabled: PropTypes.bool,
    tierPriceId: PropTypes.string,
    tierPrice: PropTypes.number,
    isEarlyTier: PropTypes.bool,
    setTierPriceId: PropTypes.func,
  }),
  withState('userHasTierSubscription', 'setUserHasTierSubscription', false),
  withHandlers({
    tierSubscriptionForUser: ({ client }) => () => {
      // Simple wrapper for getting a users current tier subscription
      return new Promise(resolve => {
        client
          .query({
            query: tierSubscriptionForUserQuery,
            fetchPolicy: 'network-only',
          })
          .then(({ data }) => {
            resolve(data.tierSubscriptionForUser)
          })
          .catch(error => {
            sendToSentry(error, {
              name: 'withTieredRegistration Error',
              message: `tierSubscriptionForUser query on ${window.location.pathname}`,
            })
          })
      })
    },
  }),
)

export type WithTieredRegistrationProps = {
  /**
   * returns boolean based on the Optimizely "tiered_registration" feature flag status.
   */
  isTieredRegEnabled: boolean
  /**
   * return value of the user's selected tierPriceId or null if they don't currently have one. This will also
   * return the default preferredTierPrice.id in the event the user is in a product-specific funnel.
   */
  tierPriceId: string | null
  /**
   * Using the user's selected tierPriceId, we use the subscription_service to resolve the dollar price amount.
   * If the user doesn't have a selected tierPriceId, the default tierPrice will be null.
   * In the event the user is in a product-specific funnel, we will resolve the default preferredTierPrice.id for
   * that specific product.
   */
  tierPrice: number | null
  /**
   * Returns true if the user's tier includes Early.
   */
  isEarlyTier: boolean
  /**
   * This will subscribe the user to a tier based on their selected tier by tierPriceId.
   * In the event that an invalid tierPriceId is given, we proceed as normal (without prompting the user)
   * and log and error to Sentry.
   * This takes an optional `tierPriceArg` which will be used in the event where a user isn't specifically picking
   * a tier option (such as a product-specific funnel).
   */
  subscribeUserToTier: (tierPriceArg?: string) => Promise<void>
}

export default withTieredRegistration
